import { useMutation } from '@tanstack/react-query';

import { AuthType, TenantType } from '@portals/types';

import { AUTH_BASE_URL, authQueryKeys, AUTH_API_URL } from './auth.constants';
import { useApiQuery } from '../hooks';
import { ServerError } from '../types/common';
import { fetchApiRequest } from '../utils/common';

export function useGoogleSSOEnabled() {
  return useApiQuery<boolean>(
    `${AUTH_BASE_URL}/google/enabled`,
    authQueryKeys.google.enabled(),
    {
      staleTime: 0,
    }
  );
}

interface UseSignInParams {
  token: string;
  tenant_type: TenantType;
  is_support_user?: boolean;
}

export function useSignIn() {
  return useMutation<AuthType, ServerError, UseSignInParams>({
    mutationFn: ({ token, tenant_type, is_support_user }) =>
      fetchApiRequest(`${AUTH_API_URL}/descope/login`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          cache: 'no-store',
        }),
        body: JSON.stringify({
          token,
          accepted_terms_and_conditions: true,
          tenant_type,
          is_support_user,
        }),
      }),
  });
}

interface UseForgotPasswordParams {
  email: string;
  tenantType: TenantType;
}

export function useForgotPassword() {
  return useMutation<{ message: string }, ServerError, UseForgotPasswordParams>(
    {
      mutationFn: ({ email, tenantType }) =>
        fetchApiRequest(`${AUTH_API_URL}/descope/reset_password`, {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
            cache: 'no-store',
          }),
          body: JSON.stringify({
            email,
            tenant_type: tenantType,
          }),
        }),
    }
  );
}

interface UseCheckUserDomainResponse {
  domain: string | null;
  partner_name: string;
}

export function useCheckUserDomain() {
  return useMutation<
    UseCheckUserDomainResponse,
    ServerError,
    { email: string }
  >({
    mutationFn: ({ email }) =>
      fetchApiRequest(`${AUTH_API_URL}/descope/check_domain`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          cache: 'no-store',
        }),
        body: JSON.stringify({
          email,
        }),
      }),
  });
}
